import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid2,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Slider,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  RestoreOutlined as ResetIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  resetSettings,
  exportSettings,
  importSettings,
  validateSettings,
  DEFAULT_SETTINGS
} from '../utils/settings';
import { useSettings } from '../hooks/useSettings';

const Settings = () => {
  const { settings: currentSettings, updateSettings } = useSettings();
  const [settings, setSettings] = useState(currentSettings);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  useEffect(() => {
    setSettings(currentSettings);
  }, [currentSettings]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setIsDirty(true);
  };

  const handleSave = () => {
    const validation = validateSettings(settings);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      showToast('error', 'Please fix validation errors before saving');
      return;
    }

    try {
      updateSettings(settings);
      setIsDirty(false);
      setValidationErrors([]);
      showToast('success', 'Settings saved successfully!');
    } catch (error) {
      showToast('error', 'Failed to save settings');
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      try {
        const defaultSettings = resetSettings();
        setSettings(defaultSettings);
        setIsDirty(false);
        setValidationErrors([]);
        showToast('success', 'Settings reset to defaults');
      } catch (error) {
        showToast('error', 'Failed to reset settings');
      }
    }
  };

  const handleExport = () => {
    try {
      const settingsJson = exportSettings();
      const blob = new Blob([settingsJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'glyder-settings.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('success', 'Settings exported successfully');
    } catch (error) {
      showToast('error', 'Failed to export settings');
    }
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedSettings = importSettings(e.target.result);
        updateSettings(importedSettings);
        setSettings(importedSettings);
        setIsDirty(false);
        setValidationErrors([]);
        showToast('success', 'Settings imported successfully');
      } catch (error) {
        showToast('error', 'Failed to import settings. Please check the file format.');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const showToast = (type, message) => {
    const toastConfig = {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    switch (type) {
      case "success":
        toast.success(message, toastConfig);
        break;
      case "error":
        toast.error(message, toastConfig);
        break;
      case "warning":
        toast.warn(message, toastConfig);
        break;
      default:
        toast.info(message, toastConfig);
    }
  };

  const getFieldError = (field) => {
    return validationErrors.find(error => error.toLowerCase().includes(field.toLowerCase().replace(/([A-Z])/g, ' $1')));
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom>
            Trading Settings
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export Settings">
              <IconButton onClick={handleExport} color="primary">
                <ExportIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Import Settings">
              <IconButton component="label" color="primary">
                <ImportIcon />
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  style={{ display: 'none' }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset to Defaults">
              <IconButton onClick={handleReset} color="warning">
                <ResetIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!isDirty}
              color="primary"
            >
              Save Changes
            </Button>
          </Stack>
        </Stack>

        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Validation Errors:</Typography>
            {validationErrors.map((error, index) => (
              <Typography key={index} variant="body2">• {error}</Typography>
            ))}
          </Alert>
        )}

        {isDirty && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have unsaved changes. Don't forget to save!
          </Alert>
        )}
      </Paper>

      <Grid2 container spacing={3}>
        {/* Trading Defaults */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Trading Defaults
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                Configure default values for stop loss, target, and position sizing
              </Typography>

              <Grid2 container spacing={3}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label="Default Stop Loss Percentage (%)"
                    type="number"
                    value={settings.defaultStopLossPercentage}
                    onChange={(e) => handleSettingChange('defaultStopLossPercentage', parseFloat(e.target.value))}
                    error={!!getFieldError('stop loss')}
                    helperText={getFieldError('stop loss') || "Negative value (e.g., -10 for 10% loss)"}
                    InputProps={{
                      inputProps: { step: 0.1, max: 0 }
                    }}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label="Default Target Percentage (%)"
                    type="number"
                    value={settings.defaultTargetPercentage}
                    onChange={(e) => handleSettingChange('defaultTargetPercentage', parseFloat(e.target.value))}
                    error={!!getFieldError('target')}
                    helperText={getFieldError('target') || "Positive value (e.g., 5 for 5% profit)"}
                    InputProps={{
                      inputProps: { step: 0.1, min: 0 }
                    }}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label="Lot Size"
                    type="number"
                    value={settings.lotSize}
                    onChange={(e) => handleSettingChange('lotSize', parseInt(e.target.value))}
                    error={!!getFieldError('lot size')}
                    helperText={getFieldError('lot size') || "Minimum trading quantity per lot"}
                    InputProps={{
                      inputProps: { step: 1, min: 1 }
                    }}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label="Default Risk Percentage (%)"
                    type="number"
                    value={settings.defaultRiskPercentage}
                    onChange={(e) => handleSettingChange('defaultRiskPercentage', parseFloat(e.target.value))}
                    error={!!getFieldError('risk percentage')}
                    helperText={getFieldError('risk percentage') || "Percentage of portfolio to risk per trade"}
                    InputProps={{
                      inputProps: { step: 0.1, min: 0.1, max: 100 }
                    }}
                  />
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>

        {/* Risk Management */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Risk Management
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                Configure risk management parameters and position sizing strategy
              </Typography>

              <Grid2 container spacing={3}>
                <Grid2 size={6}>
                  <FormControl fullWidth>
                    <InputLabel>Position Sizing Strategy</InputLabel>
                    <Select
                      value={settings.positionSizingStrategy}
                      label="Position Sizing Strategy"
                      onChange={(e) => handleSettingChange('positionSizingStrategy', e.target.value)}
                    >
                      <MenuItem value="conservative">Conservative (Lower risk)</MenuItem>
                      <MenuItem value="moderate">Moderate (Balanced risk)</MenuItem>
                      <MenuItem value="aggressive">Aggressive (Higher risk)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>

                <Grid2 size={6}>
                  <Typography gutterBottom>
                    Max Margin Utilization: {settings.maxMarginUtilization}%
                  </Typography>
                  <Slider
                    value={settings.maxMarginUtilization}
                    onChange={(e, value) => handleSettingChange('maxMarginUtilization', value)}
                    min={10}
                    max={80}
                    step={5}
                    marks={[
                      { value: 10, label: '10%' },
                      { value: 30, label: '30%' },
                      { value: 50, label: '50%' },
                      { value: 80, label: '80%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>

        {/* Data Refresh Settings */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Data Refresh Settings
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                Configure how frequently market data is refreshed
              </Typography>

              <Grid2 container spacing={3}>
                <Grid2 size={12}>
                  <FormControl fullWidth>
                    <InputLabel>Auto Refresh Interval</InputLabel>
                    <Select
                      value={settings.autoRefreshInterval}
                      label="Auto Refresh Interval"
                      onChange={(e) => handleSettingChange('autoRefreshInterval', parseInt(e.target.value))}
                    >
                      <MenuItem value={0}>Disabled</MenuItem>
                      <MenuItem value={10}>10 seconds</MenuItem>
                      <MenuItem value={30}>30 seconds</MenuItem>
                      <MenuItem value={60}>1 minute</MenuItem>
                      <MenuItem value={120}>2 minutes</MenuItem>
                      <MenuItem value={300}>5 minutes</MenuItem>
                      <MenuItem value={600}>10 minutes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>

        {/* Quick Actions */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Quick Preset Values
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                Click to quickly set common values
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Typography variant="subtitle2" gutterBottom>Stop Loss Presets:</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {[-5, -10, -15, -20, -25].map((value) => (
                      <Chip
                        key={value}
                        label={`${value}%`}
                        variant={settings.defaultStopLossPercentage === value ? "filled" : "outlined"}
                        color="error"
                        clickable
                        onClick={() => handleSettingChange('defaultStopLossPercentage', value)}
                        size="small"
                      />
                    ))}
                  </Stack>
                </Grid2>

                <Grid2 size={12}>
                  <Typography variant="subtitle2" gutterBottom>Target Presets:</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {[2, 5, 10, 15, 20, 25].map((value) => (
                      <Chip
                        key={value}
                        label={`${value}%`}
                        variant={settings.defaultTargetPercentage === value ? "filled" : "outlined"}
                        color="success"
                        clickable
                        onClick={() => handleSettingChange('defaultTargetPercentage', value)}
                        size="small"
                      />
                    ))}
                  </Stack>
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={() => setShowAlert(false)}
      >
        <Alert severity={alertSeverity} onClose={() => setShowAlert(false)}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
